import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  onSnapshot,
  serverTimestamp,
  orderBy
} from 'firebase/firestore';
import { db } from '../database/firebase';

const COLLECTION_NAME = 'socialmediauser';

/**
 * Real-time listener for CRM users
 * @param {Function} callback - Callback function to handle user updates
 * @returns {Function} - Unsubscribe function
 */
export const subscribeToUsers = (callback) => {
  const usersRef = collection(db, COLLECTION_NAME);

  const unsubscribe = onSnapshot(
    usersRef,
    (snapshot) => {
      const users = [];
      snapshot.forEach((doc) => {
        users.push({
          id: doc.id,
          ...doc.data()
        });
      });
      callback(users);
    },
    (error) => {
      console.error('Error listening to users:', error);
      callback([], error);
    }
  );

  return unsubscribe;
};

/**
 * Real-time listener for a specific user
 * @param {string} userId - User ID (phone number)
 * @param {Function} callback - Callback function to handle user updates
 * @returns {Function} - Unsubscribe function
 */
export const subscribeToUser = (userId, callback) => {
  const userRef = doc(db, COLLECTION_NAME, userId);

  const unsubscribe = onSnapshot(
    userRef,
    (doc) => {
      if (doc.exists()) {
        callback({
          id: doc.id,
          ...doc.data()
        });
      } else {
        callback(null);
      }
    },
    (error) => {
      console.error('Error listening to user:', error);
      callback(null, error);
    }
  );

  return unsubscribe;
};

/**
 * Fetch all users from Firestore
 * @returns {Promise<Array>} - Array of users
 */
export const fetchAllUsers = async () => {
  try {
    const usersRef = collection(db, COLLECTION_NAME);
    const querySnapshot = await getDocs(usersRef);
    const users = [];

    querySnapshot.forEach((doc) => {
      users.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return users;
  } catch (error) {
    console.error('Error fetching users:', error);
    throw error;
  }
};

/**
 * Fetch a single user by ID (phone number)
 * @param {string} userId - User ID (phone number)
 * @returns {Promise<Object|null>} - User object or null
 */
export const fetchUserById = async (userId) => {
  try {
    const userRef = doc(db, COLLECTION_NAME, userId);
    const userDoc = await getDoc(userRef);

    if (userDoc.exists()) {
      return {
        id: userDoc.id,
        ...userDoc.data()
      };
    }
    return null;
  } catch (error) {
    console.error('Error fetching user:', error);
    throw error;
  }
};

/**
 * Create or update a user in Firestore
 * @param {Object} userData - User data
 * @returns {Promise<Object>} - Created/updated user
 */
export const createOrUpdateUser = async (userData) => {
  try {
    const userId = userData.number; // Use phone number as ID
    const userRef = doc(db, COLLECTION_NAME, userId);
    const userDoc = await getDoc(userRef);

    const data = {
      ...userData,
      updatedAt: serverTimestamp(),
      ...(userDoc.exists() ? {} : { createdAt: serverTimestamp() })
    };

    await setDoc(userRef, data, { merge: true });

    return {
      id: userId,
      ...data
    };
  } catch (error) {
    console.error('Error creating/updating user:', error);
    throw error;
  }
};

/**
 * Update user stage
 * @param {string} userId - User ID (phone number)
 * @param {string} newStage - New stage
 * @returns {Promise<void>}
 */
export const updateUserStage = async (userId, newStage) => {
  try {
    const userRef = doc(db, COLLECTION_NAME, userId);
    await updateDoc(userRef, {
      stage: newStage,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating user stage:', error);
    throw error;
  }
};

/**
 * Add a note to user
 * @param {string} userId - User ID (phone number)
 * @param {string} noteText - Note text
 * @param {string} createdBy - User who created the note
 * @returns {Promise<Object>} - Created note
 */
export const addNote = async (userId, noteText, createdBy = 'Admin') => {
  try {
    const userRef = doc(db, COLLECTION_NAME, userId);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      throw new Error('User not found');
    }

    const note = {
      id: Date.now().toString(),
      text: noteText,
      createdAt: new Date().toISOString(),
      createdBy
    };

    const currentNotes = userDoc.data().notes || [];
    const updatedNotes = [...currentNotes, note];

    await updateDoc(userRef, {
      notes: updatedNotes,
      updatedAt: serverTimestamp()
    });

    return note;
  } catch (error) {
    console.error('Error adding note:', error);
    throw error;
  }
};

/**
 * Delete a note from user
 * @param {string} userId - User ID (phone number)
 * @param {string} noteId - Note ID
 * @returns {Promise<void>}
 */
export const deleteNote = async (userId, noteId) => {
  try {
    const userRef = doc(db, COLLECTION_NAME, userId);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      throw new Error('User not found');
    }

    const currentNotes = userDoc.data().notes || [];
    const updatedNotes = currentNotes.filter(note => note.id !== noteId);

    await updateDoc(userRef, {
      notes: updatedNotes,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error deleting note:', error);
    throw error;
  }
};

/**
 * Update user priority
 * @param {string} userId - User ID (phone number)
 * @param {string} priority - Priority level (low, medium, high)
 * @returns {Promise<void>}
 */
export const updatePriority = async (userId, priority) => {
  try {
    const userRef = doc(db, COLLECTION_NAME, userId);
    await updateDoc(userRef, {
      priority,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating priority:', error);
    throw error;
  }
};

/**
 * Update last contacted timestamp
 * @param {string} userId - User ID (phone number)
 * @returns {Promise<void>}
 */
export const updateLastContacted = async (userId) => {
  try {
    const userRef = doc(db, COLLECTION_NAME, userId);
    await updateDoc(userRef, {
      lastContactedAt: new Date().toISOString(),
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating last contacted:', error);
    throw error;
  }
};

/**
 * Delete a user
 * @param {string} userId - User ID (phone number)
 * @returns {Promise<void>}
 */
export const deleteUser = async (userId) => {
  try {
    const userRef = doc(db, COLLECTION_NAME, userId);
    await deleteDoc(userRef);
  } catch (error) {
    console.error('Error deleting user:', error);
    throw error;
  }
};

/**
 * Fetch users by stage
 * @param {string} stage - CRM stage
 * @returns {Promise<Array>} - Array of users
 */
export const fetchUsersByStage = async (stage) => {
  try {
    const usersRef = collection(db, COLLECTION_NAME);
    const q = query(usersRef, where('stage', '==', stage));
    const querySnapshot = await getDocs(q);
    const users = [];

    querySnapshot.forEach((doc) => {
      users.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return users;
  } catch (error) {
    console.error('Error fetching users by stage:', error);
    throw error;
  }
};

/**
 * Fetch leads from external API
 * @returns {Promise<Array>} - Array of leads from external API
 */
export const fetchLeadsFromExternalAPI = async () => {
  try {
    console.log('Fetching leads from external API...');
    const response = await fetch('https://zq5hwxxa0h.execute-api.us-east-1.amazonaws.com/');

    console.log('API Response status:', response.status);

    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }

    const data = await response.json();
    console.log('Raw API Response:', data);

    const allLeads = [];

    // Transform service_leads
    if (data.service_leads && Array.isArray(data.service_leads)) {
      const transformedServiceLeads = data.service_leads.map(lead => ({
        id: `api_service_${lead.id}`, // Prefix with 'api_service_' to distinguish service leads
        apiId: lead.id,
        leadType: 'service', // Identify as service lead
        name: lead.name,
        number: lead.contact,
        address: lead.address || 'N/A',
        age: lead.age,
        weight: lead.weight,
        gender: lead.gender,
        services: lead.service_type ? [lead.service_type] : [],
        meeting_time: lead.meeting_time,
        created_at: lead.created_at,
        stage: 'lead', // All API leads start in lead stage
        priority: 'medium',
        notes: [],
        appointments: [],
        source: 'external_api',
        isFromAPI: true, // Flag to identify API-only leads
        lastContactedAt: null
      }));
      allLeads.push(...transformedServiceLeads);
      console.log('Transformed service leads:', transformedServiceLeads.length);
    } else {
      console.warn('No service_leads found in API response');
    }

    // Transform business_leads
    if (data.business_leads && Array.isArray(data.business_leads)) {
      const transformedBusinessLeads = data.business_leads.map(lead => ({
        id: `api_business_${lead.id}`, // Prefix with 'api_business_' to distinguish business leads
        apiId: lead.id,
        leadType: 'business', // Identify as business lead
        name: lead.name,
        number: lead.contact,
        age: lead.age,
        location: lead.location || 'N/A',
        is_existing_business: lead.is_existing_business,
        business_name: lead.business_name,
        established_year: lead.established_year,
        current_working: lead.current_working,
        created_at: lead.created_at,
        stage: 'lead', // All API leads start in lead stage
        priority: 'medium',
        notes: [],
        appointments: [],
        source: 'external_api',
        isFromAPI: true, // Flag to identify API-only leads
        lastContactedAt: null
      }));
      allLeads.push(...transformedBusinessLeads);
      console.log('Transformed business leads:', transformedBusinessLeads.length);
    } else {
      console.warn('No business_leads found in API response');
    }

    console.log('Total transformed leads:', allLeads.length);
    return allLeads;
  } catch (error) {
    console.error('Error fetching from external API:', error);
    throw error;
  }
};

/**
 * Save API lead to Firebase when stage changes
 * @param {Object} lead - Lead data from API
 * @param {string} newStage - New stage to save
 * @returns {Promise<Object>} - Saved user data
 */
export const saveAPILeadToFirebase = async (lead, newStage) => {
  try {
    console.log('Saving API lead to Firebase:', lead, 'New stage:', newStage);

    // Use appropriate prefix based on lead type
    const leadTypePrefix = lead.leadType === 'business' ? 'api_business_' : 'api_service_';
    const userId = lead.id || `${leadTypePrefix}${lead.apiId}`;
    const userRef = doc(db, COLLECTION_NAME, userId);

    // Base user data common to both types
    const baseUserData = {
      apiId: lead.apiId || lead.id,
      leadType: lead.leadType || 'service',
      name: lead.name,
      number: lead.number,
      age: lead.age,
      api_created_at: lead.created_at,
      stage: newStage,
      priority: lead.priority || 'medium',
      notes: lead.notes || [],
      appointments: lead.appointments || [],
      source: 'external_api',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      lastContactedAt: lead.lastContactedAt || null
    };

    // Add type-specific fields
    let userData;
    if (lead.leadType === 'business') {
      // Business lead specific fields
      userData = {
        ...baseUserData,
        location: lead.location,
        is_existing_business: lead.is_existing_business,
        business_name: lead.business_name,
        established_year: lead.established_year,
        current_working: lead.current_working
      };
    } else {
      // Service lead specific fields
      userData = {
        ...baseUserData,
        address: lead.address,
        weight: lead.weight,
        gender: lead.gender,
        services: Array.isArray(lead.services) ? lead.services : (lead.services ? [lead.services] : []),
        meeting_time: lead.meeting_time
      };
    }

    await setDoc(userRef, userData, { merge: true });

    console.log('Lead saved to Firebase successfully:', userId);

    return {
      id: userId,
      ...userData,
      isFromAPI: false // Now it's saved in Firebase
    };
  } catch (error) {
    console.error('Error saving API lead to Firebase:', error);
    throw error;
  }
};

/**
 * Batch sync external API data to Firestore (legacy)
 * @param {Array} apiData - Array of user data from external API
 * @returns {Promise<Array>} - Array of synced users
 */
export const batchSyncFromAPI = async (apiData) => {
  try {
    console.log('Starting batch sync with data:', apiData);
    const syncedUsers = [];

    for (const user of apiData) {
      console.log('Processing user:', user);
      const userId = user.number;

      if (!userId) {
        console.warn('Skipping user without contact number:', user);
        continue;
      }

      const userRef = doc(db, COLLECTION_NAME, userId);
      const userDoc = await getDoc(userRef);

      // Convert services to array if it's a string
      const services = Array.isArray(user.services) ? user.services : [user.services];

      const userData = {
        name: user.name,
        number: user.number,
        address: user.address,
        services: services,
        stage: userDoc.exists() ? userDoc.data().stage : 'lead',
        notes: userDoc.exists() ? userDoc.data().notes : [],
        appointments: userDoc.exists() ? userDoc.data().appointments : [],
        createdAt: userDoc.exists() ? userDoc.data().createdAt : serverTimestamp(),
        updatedAt: serverTimestamp(),
        lastContactedAt: userDoc.exists() ? userDoc.data().lastContactedAt : null,
        priority: userDoc.exists() ? userDoc.data().priority : 'medium',
        source: 'external_api',
        // Additional fields from API
        age: user.age,
        weight: user.weight,
        gender: user.gender,
        meeting_time: user.meeting_time,
        api_created_at: user.created_at
      };

      console.log('Saving user data to Firestore:', userData);
      await setDoc(userRef, userData, { merge: true });
      syncedUsers.push({
        id: userId,
        ...userData
      });
    }

    console.log('Sync completed. Total synced users:', syncedUsers.length);
    return syncedUsers;
  } catch (error) {
    console.error('Error syncing from API:', error);
    throw error;
  }
};

/**
 * Add appointment to user
 * @param {string} userId - User ID (phone number)
 * @param {Object} appointmentData - Appointment data
 * @returns {Promise<Object>} - Created appointment
 */
export const addAppointment = async (userId, appointmentData) => {
  try {
    const userRef = doc(db, COLLECTION_NAME, userId);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      throw new Error('User not found');
    }

    const appointment = {
      id: Date.now().toString(),
      title: appointmentData.title,
      description: appointmentData.description || '',
      date: appointmentData.date,
      time: appointmentData.time,
      dateTime: new Date(`${appointmentData.date}T${appointmentData.time}`).toISOString(),
      status: 'scheduled',
      createdAt: new Date().toISOString(),
      createdBy: 'Admin'
    };

    const currentAppointments = userDoc.data().appointments || [];
    const updatedAppointments = [...currentAppointments, appointment];

    await updateDoc(userRef, {
      appointments: updatedAppointments,
      updatedAt: serverTimestamp()
    });

    return appointment;
  } catch (error) {
    console.error('Error adding appointment:', error);
    throw error;
  }
};

/**
 * Update appointment status
 * @param {string} userId - User ID (phone number)
 * @param {string} appointmentId - Appointment ID
 * @param {string} status - New status (scheduled, completed, cancelled)
 * @returns {Promise<void>}
 */
export const updateAppointmentStatusService = async (userId, appointmentId, status) => {
  try {
    const userRef = doc(db, COLLECTION_NAME, userId);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      throw new Error('User not found');
    }

    const appointments = userDoc.data().appointments || [];
    const updatedAppointments = appointments.map(apt =>
      apt.id === appointmentId ? { ...apt, status } : apt
    );

    await updateDoc(userRef, {
      appointments: updatedAppointments,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating appointment status:', error);
    throw error;
  }
};

/**
 * Delete appointment
 * @param {string} userId - User ID (phone number)
 * @param {string} appointmentId - Appointment ID
 * @returns {Promise<void>}
 */
export const deleteAppointmentService = async (userId, appointmentId) => {
  try {
    const userRef = doc(db, COLLECTION_NAME, userId);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      throw new Error('User not found');
    }

    const appointments = userDoc.data().appointments || [];
    const updatedAppointments = appointments.filter(apt => apt.id !== appointmentId);

    await updateDoc(userRef, {
      appointments: updatedAppointments,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error deleting appointment:', error);
    throw error;
  }
};

/**
 * Get today's appointments across all users
 * @returns {Promise<Array>} - Array of today's appointments with user info
 */
export const getTodayAppointments = async () => {
  try {
    const today = new Date().toISOString().split('T')[0];
    const usersRef = collection(db, COLLECTION_NAME);
    const querySnapshot = await getDocs(usersRef);

    const appointments = [];
    querySnapshot.forEach((doc) => {
      const userData = doc.data();
      if (userData.appointments) {
        userData.appointments.forEach(apt => {
          if (apt.date === today && apt.status === 'scheduled') {
            appointments.push({
              ...apt,
              userId: doc.id,
              userName: userData.name,
              userNumber: userData.number
            });
          }
        });
      }
    });

    // Sort by time
    return appointments.sort((a, b) => a.time.localeCompare(b.time));
  } catch (error) {
    console.error('Error getting today\'s appointments:', error);
    throw error;
  }
};

export default {
  subscribeToUsers,
  subscribeToUser,
  fetchAllUsers,
  fetchUserById,
  createOrUpdateUser,
  updateUserStage,
  addNote,
  deleteNote,
  updatePriority,
  updateLastContacted,
  deleteUser,
  fetchUsersByStage,
  fetchLeadsFromExternalAPI,
  saveAPILeadToFirebase,
  batchSyncFromAPI,
  addAppointment,
  updateAppointmentStatusService,
  deleteAppointmentService,
  getTodayAppointments
};
